import styled from 'styled-components';
import { remSize, prop } from '../../theme';

/** Sidebar + article, side by side; the sidebar collapses to a <select> under 770px. */
export const DocsLayout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${remSize(32)};
  max-width: ${remSize(1120)};
  width: 100%;
  margin: 0 auto;
  padding: ${remSize(16)} ${remSize(20)} ${remSize(64)};

  @media (max-width: 770px) {
    flex-direction: column;
    gap: ${remSize(12)};
    padding: ${remSize(10)};
    overflow: auto;
  }
`;

export const DocsSidebarNav = styled.nav`
  flex: 0 0 ${remSize(240)};
  position: sticky;
  top: ${remSize(16)};
  max-height: calc(100vh - ${remSize(96)});
  overflow-y: auto;
  padding-right: ${remSize(8)};
  border-right: 1px solid ${prop('Separator')};

  & h2 {
    font-size: ${remSize(12)};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${prop('inactiveTextColor')};
    margin: ${remSize(18)} 0 ${remSize(6)};
  }

  & h2:first-child {
    margin-top: 0;
  }

  & ul {
    list-style: none;
    margin: 0 0 ${remSize(4)};
    padding: 0;
  }

  & li a {
    display: block;
    padding: ${remSize(3)} ${remSize(6)};
    border-left: ${remSize(3)} solid transparent;
    font-size: ${remSize(14)};
    line-height: 1.4;
    color: ${prop('primaryTextColor')};
    text-decoration: none;
  }

  & li a:hover,
  & li a:focus {
    text-decoration: underline;
  }

  & li a[aria-current='page'] {
    border-left-color: ${prop('TabHighlight')};
    font-weight: bold;
  }

  @media (max-width: 770px) {
    position: static;
    flex: none;
    width: 100%;
    max-height: none;
    border-right: none;
    padding-right: 0;
  }
`;

/** The mobile stand-in for the sidebar. */
export const DocsSidebarSelect = styled.select`
  display: none;
  width: 100%;
  padding: ${remSize(6)};
  font-size: ${remSize(14)};
  color: ${prop('primaryTextColor')};
  background-color: ${prop('searchBackgroundColor')};
  border: 1px solid ${prop('Separator')};

  @media (max-width: 770px) {
    display: block;
  }
`;

/** Column holding the article and its prev/next footer. */
export const DocsMain = styled.main`
  flex: 1 1 auto;
  min-width: 0;
  max-width: ${remSize(760)};
  width: 100%;
`;

export const DocsArticle = styled.article`
  flex: 1 1 auto;
  min-width: 0;
  max-width: ${remSize(760)};
  line-height: 1.6;
  font-size: ${remSize(16)};
  word-break: break-word;
  overflow-wrap: anywhere;

  & p,
  & ul,
  & ol,
  & table,
  & blockquote {
    margin-bottom: ${remSize(14)};
  }

  & h1 {
    font-size: ${remSize(30)};
    line-height: 1.2;
    margin-bottom: ${remSize(12)};
  }

  & h2 {
    font-size: ${remSize(22)};
    margin: ${remSize(30)} 0 ${remSize(10)};
    padding-bottom: ${remSize(4)};
    border-bottom: 1px solid ${prop('Separator')};
  }

  & h3 {
    font-size: ${remSize(17)};
    font-weight: bold;
    margin: ${remSize(20)} 0 ${remSize(8)};
  }

  & ul,
  & ol {
    list-style: initial;
    padding-inline-start: ${remSize(28)};
  }

  & ol {
    list-style: decimal;
  }

  & li {
    margin-bottom: ${remSize(4)};
  }

  & a {
    color: ${prop('Policy.link')};
  }

  & blockquote {
    border-left: ${remSize(3)} solid ${prop('TabHighlight')};
    padding: ${remSize(2)} 0 ${remSize(2)} ${remSize(12)};
    color: ${prop('inactiveTextColor')};
  }

  /* Inline code. Block code is styled by DocsCodeBlock below. */
  & :not(pre) > code {
    font-family: Inconsolata, monospace;
    font-size: 0.95em;
    padding: 0 ${remSize(3)};
    background-color: ${prop('searchBackgroundColor')};
    border: 1px solid ${prop('Separator')};
  }

  /* No table styles: pipe tables are GFM, which react-markdown 6 doesn't parse without
     remark-gfm, so a table in a docs page renders as a paragraph of literal pipe
     characters. yarn docs:check rejects them — see scripts/check-docs.mjs. */

  & hr {
    border: none;
    border-top: 1px solid ${prop('Separator')};
    margin: ${remSize(24)} 0;
  }
`;

export const DocsCodeBlock = styled.div`
  margin-bottom: ${remSize(16)};
  border: 1px solid ${prop('Separator')};
  background-color: ${prop('searchBackgroundColor')};

  & pre {
    margin: 0;
    padding: ${remSize(10)} ${remSize(12)};
    overflow-x: auto;
  }

  & code {
    font-family: Inconsolata, monospace;
    font-size: ${remSize(13)};
    line-height: 1.5;
    white-space: pre;
  }
`;

export const DocsCodeToolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${remSize(6)};
  padding: ${remSize(4)} ${remSize(6)};
  border-bottom: 1px solid ${prop('Separator')};

  & button,
  & a {
    font-size: ${remSize(12)};
    padding: ${remSize(2)} ${remSize(8)};
    color: ${prop('primaryTextColor')};
    background: none;
    border: 1px solid ${prop('Separator')};
    text-decoration: none;
    cursor: pointer;
  }

  & button:hover,
  & a:hover {
    color: ${prop('Button.primary.hover.foreground')};
    background-color: ${prop('Button.primary.hover.background')};
    border-color: ${prop('Button.primary.hover.border')};
  }
`;

export const DocsFooterNav = styled.nav`
  display: flex;
  justify-content: space-between;
  gap: ${remSize(16)};
  margin-top: ${remSize(40)};
  padding-top: ${remSize(12)};
  border-top: 1px solid ${prop('Separator')};
  font-size: ${remSize(14)};

  & a {
    color: ${prop('Policy.link')};
  }
`;

export const DocsMessage = styled.p`
  padding: ${remSize(24)} 0;
  color: ${prop('inactiveTextColor')};
`;
